import api, { deduplicatedApi } from "./index";
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from "../lib/cache";

// CREATE lead API call
export const addLead = async (leadData) => {
  const response = await api.post("lead-management/leads/", leadData);

  // Invalidate all leads cache to ensure fresh data
  apiCache.invalidatePattern('leads-');

  return response;
};

// GET leads API call (raw - use getLeadsCached for cached version)
// Using deduplicatedApi to prevent duplicate simultaneous requests
export const getLeads = (params = {}) =>
  deduplicatedApi.get("lead-management/leads/", { params });

// GET leads with caching support
export const getLeadsCached = async (params = {}, options = {}) => {
  const { forceRefresh = false } = options;

  return cachedApiCall(
    cacheKeys.leads(params),
    () => getLeads(params),
    2 * 60 * 1000, // 2 minute cache
    { forceRefresh }
  );
};

// DELETE lead API call
// keepOffer: used by "Decline Offer" — removes the lead from the Buyer Portal Offers
// holding area but keeps the buyer's Offer on the property's Offers tab (as Declined).
export const deleteLead = async (id, { keepOffer = false } = {}) => {
  const response = await api.delete(
    `lead-management/leads/${id}/${keepOffer ? "?keep_offer=true" : ""}`
  );

  // Invalidate all leads cache to ensure fresh data
  apiCache.invalidatePattern('leads-');

  return response;
};

// Ignore / Restore an enquiry, offer, inspection request or renter enquiry.
// This is the Ignore button on the Buyer Enquiries & Offers page. It marks the
// lead so the row leaves the holding area and deletes NOTHING: the contact,
// their enquiry history and the lead itself all stay in the CRM (Christian
// Gravias, Monday 12838026421). Pass ignored: false to restore.
export const ignoreLead = async (id, { ignored = true, reason, notes } = {}) => {
  const payload = { ignored };
  if (reason) payload.reason = reason;
  if (notes) payload.ignore_notes = notes;
  const response = await api.post(`lead-management/leads/${id}/ignore/`, payload);

  apiCache.invalidatePattern('leads-');

  return response;
};

// Bulk ignore / restore, the "Ignore Selected" action on the Buyer Enquiries &
// Offers page, so an agent isn't clicking Ignore once per row. Marks, never
// deletes.
export const bulkIgnoreLeads = async (ids, { ignored = true, reason, notes } = {}) => {
  const payload = { ids, ignored };
  if (reason) payload.reason = reason;
  if (notes) payload.ignore_notes = notes;
  const response = await api.post("lead-management/leads/bulk-ignore/", payload);

  apiCache.invalidatePattern('leads-');

  return response;
};

// Seller Leads holding area — mark a lead as actioned so it drops out of the
// Seller Leads page and the Dashboard's Off / On Market Leads widgets.
// action: 'appraisal' | 'database' | 'ignored' | 'lost', or 'clear' to restore.
// 'lost' requires a `reason` (genuine lead we lost, kept distinct from 'ignored'
// = garbage). Deliberately not a delete: the Lead row still feeds Selling Leads,
// the dashboard counts and Cost Per Lead (Christian Gravias, Monday 12817261831).
// `propertyId` (on 'appraisal') is the appraisal just saved, so the lead's notes
// are copied onto it (CRM-2026-00090).
export const setSellerLeadAction = async (id, action, reason = undefined, notes = undefined, propertyId = undefined) => {
  const payload = { action };
  if (reason) payload.reason = reason;
  // Ignore's notes, or the words behind a Lost "Other" (Monday 13375284987).
  if (notes) payload[action === "lost" ? "lost_note" : "ignore_notes"] = notes;
  if (propertyId) payload.property_id = propertyId;
  const response = await api.post(`lead-management/leads/${id}/seller-action/`, payload);

  apiCache.invalidatePattern('leads-');

  return response;
};

// "Listed" in one click (Christian Gravias, Monday 13177615952): the lead leaves
// the holding area and its ONE property profile becomes a draft listing in My
// Listings / Collings Properties. The server reuses the live profile already
// at the address instead of making a second. `parts` is the parsed address
// (street / suburb / state / postal_code) so a single-line address still finds
// its suburb. A 400 with code "no_address" means: open the Add form instead.
export const markSellerLeadListed = async (id, parts = {}) => {
  const response = await api.post(`lead-management/leads/${id}/seller-action/`, {
    action: "listed",
    ...parts,
  });

  apiCache.invalidatePattern('leads-');
  invalidateCache.properties();

  return response;
};

// A seller lead card to New, Connected or Long Term on the Prospects board,
// from any of those or out of Lost (Christian Gravias, 21 Oct 2026: "any card
// can be moved anywhere"). New clears the call; a Lost lead comes back un-lost.
export const moveLeadOnBoard = async (id, to) => {
  const response = await api.post(`lead-management/leads/${id}/board-move/`, { to });

  apiCache.invalidatePattern('leads-');

  return response;
};

// The X on a card in the Prospects LOST column: takes a lost seller lead
// (kind "lead") or lost appraisal (kind "property") off the board. The record
// itself is untouched, so it still counts in every lost-lead stat.
export const hideLostFromProspects = async (kind, id) => {
  const response = await api.post(`lead-management/prospects/lost/hide/`, { kind, id });
  apiCache.invalidatePattern('leads-');
  invalidateCache.properties();
  return response;
};

// Re-assign an IGNORED lead to another agent (super-admin only). Un-ignores the
// lead (clears whichever holding-area marker put it out of view) and drops it
// back into the new agent's pipeline, who gets the standard assignment alert.
// (#12984261525 revision — "allow Christian to re-assign the lead".)
export const reassignIgnoredLead = async (id, agentId, note = "") => {
  const payload = { agent: agentId };
  if (note && note.trim()) payload.note = note.trim();
  const response = await api.post(`lead-management/leads/${id}/reassign-ignored/`, payload);
  apiCache.invalidatePattern("leads-");
  return response;
};

// Add lead (buyer enquiry) to property enquiries
export const addLeadToPropertyEnquiry = async (leadId, propertyId, message = null) => {
  const response = await api.post(`lead-management/leads/${leadId}/add-to-property-enquiry/`, {
    property_id: propertyId,
    message: message,
  });
  return response;
};

// The reminder series on a lead still in the holding area (Simon Abbott,
// Monday 13166757620 / CRM-2026-00083). Same shape as the appraisal series
// endpoint in api/agent.js, so AppraisalReminderSeries drives both.
export const getLeadReminders = (leadId, params = {}) =>
  api.get(`lead-management/leads/${leadId}/reminders/`, { params });

// `reminders` is [{ date: ISO string, reason }], `note` the note typed with them.
export const createLeadReminders = (leadId, reminders, note = "") =>
  api.post(
    `lead-management/leads/${leadId}/reminders/`,
    { reminders, note },
    { headers: { "Content-Type": "application/json" } }
  );

export const deleteLeadReminder = (leadId, kind, reminderId) =>
  api.delete(`lead-management/leads/${leadId}/reminders/${kind}/${reminderId}/`);

// UPDATE lead API call
export const updateLead = async (id, leadData) => {
  const response = await api.put(`lead-management/leads/${id}/`, leadData);

  // Invalidate all leads cache to ensure fresh data
  apiCache.invalidatePattern('leads-');

  return response;
};
